import React, { useState } from 'react';
import {
  Box,
  Grid,
  Snackbar,
  Alert,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Typography,
} from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login';
import Register from './components/Register';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import EmailSender from './EmailSender';
import Campaigns from './components/Campaigns';
import Contacts from './components/Contacts';
import TemplateBuilder from './components/TemplateBuilder';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import Unsubscribe from './components/Unsubscribe';
import Settings from './components/Settings';
import EmailValidator from './components/EmailValidator';
import { EmailTemplate, AlertInfo } from './types';

// Create a custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
    },
    secondary: {
      main: '#764ba2',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

// Main App Layout Component (for authenticated routes)
const MainAppLayout: React.FC = () => {
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [alertInfo, setAlertInfo] = useState<AlertInfo>({
    open: false,
    message: '',
    severity: 'info'
  });

  const handleViewChange = (view: string) => {
    setCurrentView(view);
  };

  const handleTemplateSelect = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setCurrentView('compose'); // Switch to compose view
  };

  const showAlert = (message: string, severity: AlertInfo['severity']) => {
    setAlertInfo({ open: true, message, severity });
    setTimeout(() => {
      setAlertInfo(prev => ({ ...prev, open: false }));
    }, 6000);
  };

  const handleCloseAlert = () => {
    setAlertInfo(prev => ({ ...prev, open: false }));
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return <Dashboard showAlert={showAlert} />;
      case 'compose':
        return <EmailSender selectedTemplate={selectedTemplate} />;
      case 'campaigns':
        return <Campaigns showAlert={showAlert} onNavigate={handleViewChange} />;
      case 'contacts':
        return <Contacts showAlert={showAlert} />;
      case 'templates':
        return <TemplateBuilder onTemplateSelect={handleTemplateSelect} showAlert={showAlert} />;
      case 'email-validator':
        return <EmailValidator />;
      case 'analytics':
        return <AnalyticsDashboard showAlert={showAlert} />;
      case 'settings':
        return <Settings showAlert={showAlert} />;
      default:
        return <Dashboard showAlert={showAlert} />;
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Sidebar - Fixed */}
      <Box sx={{ width: 280, flexShrink: 0 }}>
        <Sidebar currentView={currentView} onViewChange={handleViewChange} />
      </Box>

      {/* Main Content Area */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Main Content - Scrollable */}
        <Box
          className="main-content"
          sx={{
            flexGrow: 1,
            p: 4,
            overflow: 'auto',
            backgroundColor: 'white',
            borderRadius: '12px 0 0 0',
            boxShadow: '-2px 0 10px rgba(0, 0, 0, 0.1)',
          }}
        >
          {renderCurrentView()}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 3,
            backgroundColor: 'white',
            borderTop: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Grid container spacing={2} justifyContent="center" alignItems="center">
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                © 2024 Email Outreach Platform
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                Built with ❤️ for better email campaigns
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                Version 1.0.0 • All rights reserved
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Alert Snackbar */}
      <Snackbar open={alertInfo.open} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert severity={alertInfo.severity} onClose={handleCloseAlert}>
          {alertInfo.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Main App Component with Routing
const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />

            {/* Protected routes */}
            <Route path="/*" element={
              <ProtectedRoute>
                <MainAppLayout />
              </ProtectedRoute>
            } />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
